/** Wraps one job of long-running work. Callers that have no lane pass `runInline`,
 *  which just runs it. */
export type SerialLane = <T>(job: () => Promise<T>) => Promise<T>

/** No lane — the job runs inline, as it would without one. */
export const runInline: SerialLane = (job) => job()

/** Hands the rest of the frame back to the browser: pending input and paint go
 *  first, then the next job. `scheduler.yield` resumes at user-visible priority;
 *  a macrotask hop is the fallback where it isn't implemented. */
function yieldToBrowser(): Promise<void> {
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler
  if (typeof scheduler?.yield === 'function') return scheduler.yield()
  return new Promise((resolve) => {
    setTimeout(resolve, 0)
  })
}

/**
 * A serial lane for long, uninterruptible work — image decodes, pixel scans,
 * canvas encodes — on whichever thread it runs. Jobs run in the order they were
 * queued, each after a yield, and a rejected job doesn't break the lane for the
 * ones behind it; only the caller of a failed job sees its error.
 *
 * Two reasons to put such work in a lane rather than running it N-wide:
 *  - Latency: the thread gets a task boundary between jobs, so on the main thread
 *    a click waits out one job instead of the whole pile. It bounds the wait
 *    rather than removing it — a single job is still not interruptible.
 *  - Peak memory: N decodes in flight means N full-size pixel buffers alive at
 *    once, and the GC pauses that come with them. This is why a worker wants a
 *    lane too: moving the work off the main thread makes the stalls invisible,
 *    not the memory.
 *
 * Nothing is gained by running such work concurrently on one thread: it cannot
 * overlap. Network waits, by contrast, belong OUTSIDE a lane job — holding the
 * lane across a request idles the thread and serializes the requests too.
 */
export function createSerialLane(): SerialLane {
  let tail: Promise<unknown> = Promise.resolve()

  return <T>(job: () => Promise<T>): Promise<T> => {
    const result = tail.then(async () => {
      await yieldToBrowser()
      return job()
    })
    // The lane's own chain must never reject, or every job behind a failure would
    // be rejected with someone else's error.
    tail = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }
}
